import { Node } from '@tiptap/core';
import { mergeAttributes } from '@tiptap/react';
import { Suggestion, SuggestionOptions } from '@tiptap/suggestion';
import { PluginKey } from '@tiptap/pm/state';

interface EmojiNodeAttrs {
  /**
   * The label to be rendered by the editor as the displayed text for emoji
   * item, if provided.
   */
  label?: string | null;
}

type EmojiOptions<
  SuggestionItem = any,
  Attrs extends Record<string, any> = EmojiNodeAttrs,
> = {
  /**
   * The suggestion options.
   * @default {}
   * @example { char: ':', pluginKey: EmojiPluginKey, command: ({ editor, range, props }) => { ... } }
   */
  suggestion: Omit<SuggestionOptions<SuggestionItem, Attrs>, 'editor'>;
};

declare module '@tiptap/core' {
  // eslint-disable-next-line no-unused-vars
  interface Commands<ReturnType> {
    emoji: {
      /**
       * Insert emoji
       * @param attributes The emoji attributes
       * @example editor.commands.insertEmoji({ label: 'unicode' })
       */
      insertEmoji: (attributes: EmojiNodeAttrs) => ReturnType;
    };
  }
}

const EmojiPluginKey = new PluginKey('emoji');

const EmojiExtension = Node.create<EmojiOptions>({
  name: 'emoji',
  group: 'inline',
  inline: true,
  selectable: true,
  // we want it as non editable node
  // https://tiptap.dev/docs/editor/core-concepts/schema#atom
  atom: true,

  addOptions() {
    return {
      HTMLAttributes: {},
      deleteTriggerWithBackspace: false,
      suggestion: {
        char: ':',
        pluginKey: EmojiPluginKey,
        allow: ({ state, range }) => {
          const $from = state.doc.resolve(range.from);
          const type = state.schema.nodes[this.name];
          return !!$from.parent.type.contentMatch.matchType(type);
        },
      },
    };
  },

  // add attributes to the custom node, these appear on the model/schema
  addAttributes() {
    return {
      label: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-label'),
        renderHTML: (attributes) => {
          if (!attributes.label) {
            return {};
          }

          return {
            'data-label': attributes.label,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: `span[data-type="${this.name}"]`,
      },
    ];
  },

  renderHTML({ HTMLAttributes, node }) {
    return [
      'span',
      mergeAttributes({ 'data-type': this.name }, HTMLAttributes),
      node.attrs.label,
    ];
  },

  // allow to programmatically insert the emoji
  // user should be able to insert emoji node from editor command
  // create `insertEmoji` command
  addCommands() {
    return {
      insertEmoji:
        (attributes) =>
        ({ chain }) => {
          return chain()
            .insertContent({
              type: this.name,
              attrs: {
                label: attributes.label,
              },
            })
            .insertContent(' ')
            .run();
        },
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },
});

export default EmojiExtension;
